import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchLandingData } from '../services/landing.api';
import type { LandingDynamicData, LandingPageData } from '../types';

const EMPTY_DATA: LandingDynamicData = {
  stats: {
    completed_deliveries: 0,
    coverage_areas: 0,
    average_rating: 4.8,
    average_delivery_hours: null,
    active_drivers: 0,
  },
  drivers_preview: [],
  payment_methods: [],
  tracking_preview: null,
  sample_tracking_codes: [],
};

const LandingDataContext = createContext<LandingPageData>({
  ...EMPTY_DATA,
  loading: true,
  error: null,
});

export function LandingDataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<LandingDynamicData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLandingData()
      .then(setData)
      .catch(() => setError('Impossible de charger les données de la plateforme.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <LandingDataContext.Provider value={{ ...data, loading, error }}>
      {children}
    </LandingDataContext.Provider>
  );
}

export function useLandingData(): LandingPageData {
  return useContext(LandingDataContext);
}

export function formatLandingStat(
  key: keyof LandingDynamicData['stats'],
  stats: LandingDynamicData['stats'],
): string {
  switch (key) {
    case 'completed_deliveries':
      return stats.completed_deliveries >= 1000
        ? `${Math.floor(stats.completed_deliveries / 1000)}k+`
        : String(stats.completed_deliveries);
    case 'coverage_areas':
      return `${stats.coverage_areas} zone${stats.coverage_areas > 1 ? 's' : ''}`;
    case 'average_rating':
      return `${stats.average_rating}/5`;
    case 'average_delivery_hours':
      if (stats.average_delivery_hours == null) return '< 2h';
      return stats.average_delivery_hours < 1
        ? `${Math.round(stats.average_delivery_hours * 60)} min`
        : `< ${Math.ceil(stats.average_delivery_hours)}h`;
    case 'active_drivers':
      return String(stats.active_drivers);
    default:
      return '—';
  }
}
